import React from "react";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import { Quote, Scale, Award, Shield, Check, ArrowRight, Gavel, Users, FileText } from "lucide-react";
import img from "../../assets/images/Founder-image/founder.jpg";
import { useNavigate } from "react-router-dom";

const AboutPageSection = () => {
  const brandGold = "#C8A27C";
  const navigate = useNavigate();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <div className="bg-[#FAF9F6] text-[#080C10] font-sans selection:bg-[#C8A27C] selection:text-white overflow-hidden">
      
      {/* --- 1. HERO SECTION: THE ADVOCATE --- */}
      <section className="relative min-h-screen flex items-center px-6 lg:px-20 py-20">
        <div className="absolute top-0 right-0 w-full lg:w-1/3 h-full bg-[#080C10] z-0 hidden lg:block" />
        
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 items-center relative z-10">
          
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-[1px] bg-[#C8A27C]" />
              <p className="uppercase tracking-[0.4em] text-[10px] font-black text-[#C8A27C]">
                Ghaziabad • Uttar Pradesh • India
              </p>
            </div>

            <h1 className="text-5xl md:text-7xl font-serif font-medium leading-[1.1] mb-8">
              Expert Advocacy. <br />
              <span className="italic font-light text-[#C8A27C]">Uncompromising </span>
              Results.
            </h1>

            <p className="text-gray-500 text-lg leading-relaxed max-w-xl mb-10 font-light">
              Advocate Saurav Dagar provides specialized legal counsel in Civil, Criminal, and Matrimonial litigation. Based in Ghaziabad, he combines rigorous legal research with a client-first approach to navigate the complexities of the Indian judicial system.
            </p>

            <div className="flex flex-wrap gap-5">
              <button 
                onClick={() => navigate("/services")}
                className="group flex items-center gap-3 px-10 py-5 bg-[#080C10] text-white hover:bg-[#C8A27C] transition-all duration-500 rounded-sm text-[11px] font-bold uppercase tracking-widest shadow-2xl"
              >
                <span>Practice Areas</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              
              <button
                onClick={() => navigate("/contact-us")}
                className="px-10 py-5 border border-[#080C10]/10 text-[#080C10] hover:border-[#C8A27C] hover:text-[#C8A27C] transition-all duration-500 rounded-sm text-[11px] font-bold uppercase tracking-widest bg-transparent"
              >
                Book a Consultation
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-[12px] border-white group">
              <img
                src={img}
                alt="Advocate Saurav Dagar"
                className="w-full aspect-[4/5] object-cover transition-transform duration-1000 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080C10] via-transparent to-transparent opacity-60" />
              <div className="absolute bottom-0 left-0 p-8">
                 <h3 className="text-white text-3xl font-serif mb-1">Saurav Dagar</h3>
                 <p className="text-[#C8A27C] text-[10px] uppercase tracking-[0.3em] font-black">Advocate | District Court Ghaziabad</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- 2. BENTO EXPERTISE GRID --- */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="text-4xl font-serif mb-4">Core Legal <span className="italic text-[#C8A27C]">Specializations</span></h2>
            <p className="text-gray-400 font-light text-sm">Providing strategic representation across the most critical legal domains.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <ExpertiseCard 
              icon={<Gavel />} 
              title="Criminal Defense" 
              desc="Aggressive representation in bail matters, trials, and appeals within the Criminal Justice system."
            />
            <ExpertiseCard 
              icon={<Users />} 
              title="Matrimonial Law" 
              desc="Empathetic yet firm handling of divorce, child custody, and maintenance disputes."
            />
            <ExpertiseCard 
              icon={<FileText />} 
              title="Civil Litigation" 
              desc="Expertise in property disputes, recovery suits, and accidental (MACT) claims."
            />
          </div>
        </div>
      </section>

      {/* --- 3. THE JUSTICE QUOTE --- */}
      <section className="py-24 bg-[#080C10] relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center px-6 relative z-10">
          <Quote className="mx-auto text-[#C8A27C]/30 mb-8" size={50} />
          <h2 className="text-2xl md:text-4xl font-serif italic text-white/90 leading-snug mb-10">
            "Justice is not merely about winning cases; it is about restoring balance and ensuring that the rule of law protects every individual’s rights."
          </h2>
          <div className="w-16 h-[1px] bg-[#C8A27C] mx-auto mb-4" />
          <p className="text-white text-[10px] uppercase tracking-[0.5em] font-black">Adv. Saurav Dagar</p>
        </div>
      </section>

      {/* --- 4. COUNTER SECTION --- */}
      <section ref={ref} className="py-24 bg-[#FAF9F6] border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
            {[
              { label: "Cases Handled", value: 500, suffix: "+" },
              { label: "Success Rate", value: 92, suffix: "%" },
              { label: "Satisfied Clients", value: 400, suffix: "+" },
              { label: "Years in Practice", value: 8, suffix: "+" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-4xl lg:text-6xl font-serif text-[#080C10] mb-2">
                  {inView ? <CountUp end={stat.value} duration={2} /> : 0}{stat.suffix}
                </p>
                <p className="text-[10px] uppercase tracking-[0.3em] font-black text-gray-400">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
};

/* Helper Component */
const ExpertiseCard = ({ icon, title, desc }) => (
  <div className="p-10 bg-[#FAF9F6] border border-gray-100 rounded-xl hover:border-[#C8A27C]/30 transition-all duration-500 group">
    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-[#C8A27C] mb-8 shadow-sm group-hover:bg-[#C8A27C] group-hover:text-white transition-all">
      {React.cloneElement(icon, { size: 22 })}
    </div>
    <h4 className="text-sm font-black uppercase tracking-widest mb-4">{title}</h4>
    <p className="text-gray-500 text-xs leading-relaxed font-light">{desc}</p>
  </div>
);

export default AboutPageSection;